import { formatEasternTime, isEasternSunday, sundayDeadlineFor } from "./marketAge.js";

// Sunday-morning games: a Sunday (Eastern) kickoff before the week's Sunday
// 1:00pm ET deadline -- the London and Germany games at 9:30am. They lock at
// their own kickoff, before the 1pm games' prices have settled, so the picker
// has to decide on one without seeing what it is competing with.
//
// The rule: a morning game is worth a slot only at `winProbability` >= this,
// in percentage points (edge.js units). Where it came from: last season's
// 5th-best Sunday pick averaged 52.4%; in 2026 the 5th-best 1pm pick finished
// at 51.1-52.3% (Weeks 1-7); and from 9:30am to 1pm the 5th-best rose about
// +0.5 point on average (n=4). A morning game under it would most likely be
// bumped out of the top five by 1pm. It is a constant, reviewed at season end
// (DEV-326), not refitted automatically.
//
// Read by the Home card's take-or-wait box, by "pick the top 5"
// (`topFive.js`) and by the You vs App board (`seasonScorecard.js`), so the
// three cannot disagree about which morning games are worth a slot.
export const MORNING_GAME_BAR = 52.5;

// The 5th-best 1pm pick's finishing win % across 2026 Weeks 1-7. Printed in
// the box's reason line, so it names the weeks it covers rather than claiming
// "every week this season" once the season runs past them.
export const FIFTH_PICK_RANGE = { low: 51.1, high: 52.3, throughWeek: 7 };

// The box's track runs over this window of win %.
export const MORNING_TRACK = { low: 48, high: 58 };

/** A Sunday (Eastern) kickoff before that Sunday's 1:00pm ET deadline. */
export function isMorningGame(kickoffAt) {
  if (!isEasternSunday(kickoffAt)) return false;
  const deadline = sundayDeadlineFor(kickoffAt);
  return deadline != null && new Date(kickoffAt).getTime() < deadline;
}

/** Whether a morning game's win % earns it a slot. */
export function clearsMorningBar(winProbability) {
  return winProbability != null && winProbability >= MORNING_GAME_BAR;
}

/**
 * Whether a game may take a slot under the morning rule: any game that is not
 * a morning game, and a morning game at or over the bar.
 */
export function passesMorningRule(kickoffAt, winProbability) {
  return !isMorningGame(kickoffAt) || clearsMorningBar(winProbability);
}

function trackAt(win) {
  const { low, high } = MORNING_TRACK;
  return Math.max(0, Math.min(100, ((win - low) / (high - low)) * 100));
}

/**
 * The Home card's take-or-wait call for one game, or null when the card shows
 * no box: not a morning game, already kicked off, no frozen line, or no market
 * to price it.
 *
 * `game` is a stored (camelCase) game; `edge` is its `computeEdge`.
 */
export function morningCall(game, edge, now = Date.now()) {
  const kickoffAt = game?.kickoffAt;
  if (!isMorningGame(kickoffAt)) return null;
  const kickoff = new Date(kickoffAt).getTime();
  if (now >= kickoff) return null;
  if (game.fivePicksSpread == null) return null;
  if (!edge?.measurable || edge.winProbability == null) return null;

  const win = edge.winProbability;
  const take = clearsMorningBar(win);
  const { low, high, throughWeek } = FIFTH_PICK_RANGE;
  const time = formatEasternTime(kickoff).replace(/(am|pm)$/, " $1").toUpperCase();

  return {
    take,
    kicker: `MORNING GAME · LOCKS AT ${time}`,
    call: take ? "Take it" : "Wait for 1pm",
    reason: `${take ? "Over" : "Under"} the ${MORNING_GAME_BAR}% bar`,
    note: take
      ? `A 1pm game rarely does better: the 5th-best pick finished between ${low}% and ${high}% in every week through Week ${throughWeek}.`
      : `By 1pm the 5th-best game usually reaches ${Math.floor(low)}–${Math.floor(high)}%, so this one would most likely be bumped out of the top 5.`,
    barAt: trackAt(MORNING_GAME_BAR),
    dotAt: trackAt(win),
    barLabel: `bar ${MORNING_GAME_BAR}%`,
  };
}
